import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { forkJoin, from, map, Observable, of, switchMap } from 'rxjs';
import { environment } from '../../../../environments/environment';

import {
  EditForm,
  ReportFile,
  ReportReason,
  ReportSubmitPayload,
  Report,
  ReportEntityDetails,
} from '../../interfaces/report-content';
import { PaginatedResponse } from '../../interfaces/pagination';
import { FileUploadService } from '../../../pages/documents/services/file-upload.service';

@Injectable({
  providedIn: 'root',
})
export class ReportsService {
  private apiUrl = environment.apiBaseUrl;
  private http = inject(HttpClient);
  private fileUploadService = inject(FileUploadService);

  getReportReasons(
    entity_type: string,
    apiKey?: string,
  ): Observable<ReportReason[]> {
    const headers: Record<string, string> = {};
    if (apiKey) {
      headers['x-api-key'] = apiKey;
    }

    return this.http.get<ReportReason[]>(
      `${this.apiUrl}/api/reports/reasons/${entity_type}`,
      { headers },
    );
  }

  getUserReports(
    page = 1,
    pageSize = 50,
    entity_type: string | null,
    severity: number | null,
    status: string | null,
  ): Observable<PaginatedResponse<Report>> {
    let params = new HttpParams();
    params = params.set('page', page.toString());
    params = params.set('page_size', pageSize.toString());
    if (entity_type) {
      params = params.set('entity_type', entity_type);
    }
    if (severity) {
      params = params.set('severity', severity);
    }
    if (status) {
      params = params.set('status', status);
    }

    return this.http.get<PaginatedResponse<Report>>(
      `${this.apiUrl}/api/reports`,
      {
        params,
        headers: {
          'X-Skip-Loader': 'true',
        },
      },
    );
  }

  createReport(payload: ReportSubmitPayload): Observable<void> {
    const headers: Record<string, string> = {};

    return this.http.post<void>(`${this.apiUrl}/api/reports/`, payload, {
      headers,
    });
  }

  updateReport(reportId: number, payload: Partial<EditForm>): Observable<void> {
    return this.http.patch<void>(
      `${this.apiUrl}/api/admin/reports/${reportId}`,
      payload,
    );
  }

  getAllReports(
    page = 1,
    pageSize = 50,
    entity_type: string | null,
    severity: number | null,
    status: string | null,
  ): Observable<PaginatedResponse<Report>> {
    let params = new HttpParams();
    params = params.set('page', page.toString());
    params = params.set('page_size', pageSize.toString());
    if (entity_type) {
      params = params.set('entity_type', entity_type);
    }
    if (severity) {
      params = params.set('severity', severity);
    }
    if (status) {
      params = params.set('status', status);
    }

    return this.http.get<PaginatedResponse<Report>>(
      `${this.apiUrl}/api/admin/reports`,
      {
        params,
        headers: {
          'X-Skip-Loader': 'true',
        },
      },
    );
  }

  uploadReportFiles(
    files: File[],
    entity_id: number | string,
    entity_type: string,
    reason_id: number,
  ): Observable<ReportFile[]> {
    if (!files?.length) {
      return of([]);
    }

    const fileNames = files.map((file) => file.name);

    return this.http
      .post<ReportFile[]>(`${this.apiUrl}/api/reports/upload`, {
        file_names: fileNames,
        entity_id: entity_id,
        entity_type: entity_type,
        reason_id: reason_id,
      })
      .pipe(
        switchMap((uploadResponses: ReportFile[]) => {
          if (!uploadResponses?.length) {
            return of([]);
          }

          const uploadRequests = uploadResponses.map((uploadResponse) => {
            const file = files.find(
              (item) => item.name === uploadResponse.blob_name,
            );

            if (!file) {
              return of(uploadResponse);
            }

            return from(
              this.fileUploadService.uploadWithSDK(
                uploadResponse.url,
                file,
                uploadResponse.file_id.toString(),
              ),
            ).pipe(map(() => uploadResponse));
          });

          return forkJoin(uploadRequests);
        }),
      );
  }

  getEntityDetails(
    entityType: string,
    entityId: number | string,
  ): Observable<ReportEntityDetails> {
    return this.http.get<ReportEntityDetails>(
      `${this.apiUrl}/api/admin/reports/entity/${entityType}/${entityId}`,
    );
  }

  reportEmail(payload: ReportSubmitPayload, token?: string): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/api/reports/email`, payload, {
      params: token ? { token } : {},
    });
  }
}
